import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { ForgotPasswordSchema } from "../../FormsValidations/Validations";
import logoImage from "../../assets/images/logoNew.jpg";
import InlineAlert from "../../Utility/InlineAlert/InlineAlert";
import UseTitle from "../../Utility/UseTitle/UseTitle";
import Typography from "../../Utility/Typography";
import Label from "../../Utility/Label/Label";
import Button from "../../Utility/Button/Button";
import FormSubmitButton from "../../Utility/Button/FormSubmitButton";
import { post } from "../../Services/ApiBase";
import { API_ENDPOINTS } from "../../Services/ApiEndPoints";
import { logger } from "../../utils/logger";
import Input from "../../Utility/Input/Input";

export default function ForgotPassword() {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(ForgotPasswordSchema),
    defaultValues: {
      Email: "",
    },
  });

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [localError, setLocalError] = useState("");
  const [success, setSuccess] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");

  const sendResetEmail = async (email) => {
    await post(API_ENDPOINTS.AUTH.FORGOT_PASSWORD, {
      email: (email || "").trim(),
    });
  };

  const onSubmit = async (data) => {
    try {
      setLocalError("");
      setLoading(true);

      const email = (data.Email || "").trim();
      await sendResetEmail(email);
      setSubmittedEmail(email);
      setSuccess(true);
    } catch (err) {
      logger.error("Forgot password failed:", err);
      if (typeof err === "object") {
        const apiMsg = err?.response?.data?.data?.message ?? err?.response?.data?.message;
        if (apiMsg) {
          setLocalError(apiMsg);
        } else if (err?.message) {
          setLocalError(err.message);
        } else {
          setLocalError("Something went wrong. Please try again.");
        }
      } else {
        setLocalError("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = async () => {
    if (!submittedEmail || resendLoading) return;
    try {
      setLocalError("");
      setResendLoading(true);
      await sendResetEmail(submittedEmail);
    } catch (err) {
      logger.error("Resend forgot password email failed:", err);
      if (typeof err === "object") {
        const apiMsg = err?.response?.data?.data?.message ?? err?.response?.data?.message;
        if (apiMsg) {
          setLocalError(apiMsg);
        } else if (err?.message) {
          setLocalError(err.message);
        } else {
          setLocalError("Something went wrong. Please try again.");
        }
      } else {
        setLocalError("Something went wrong. Please try again.");
      }
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div>
      <UseTitle formattedTitle="Forgot Password" />
      <div className="auth-page-wrapper">
        <div className="auth-page-content pb-0">
          <div className="w-full max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-center h-screen">
              <div className="w-full md:w-2/3 lg:w-1/2 xl:w-2/5">
                <div className="bg-surface-primary rounded-md border border-border-primary shadow-sm mt-4">
                  <div className="mt-5">
                    <div className="flex justify-center items-center">
                      <img
                        src={logoImage}
                        alt="Logo"
                        className="h-[72px] w-auto max-w-[280px] object-contain"
                      />
                    </div>
                  </div>
                  <div className="p-4 mt-4">
                    {localError && (
                      <InlineAlert className="mb-3" severity="error">
                        {localError}
                      </InlineAlert>
                    )}
                    {success && (
                      <InlineAlert className="mb-3" severity="success">
                        Password reset instructions have been sent to your email address. Please
                        check your inbox.
                      </InlineAlert>
                    )}
                    {!success ? (
                      <div className="p-2">
                        <Typography variant="h4" className="text-center mb-4 text-theme-primary">
                          Forgot Password?
                        </Typography>
                        <Typography variant="text2" className="text-center mb-6 text-gray-600">
                          Enter your email address and we'll send you instructions to reset your
                          password.
                        </Typography>
                        <form onSubmit={handleSubmit(onSubmit)}>
                          <div className="mb-3">
                            <Label htmlFor="email">Email</Label>
                            {/* <input
                              autoComplete="off"
                              type="email"
                              className={`w-full text-sm border rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${
                                errors.Email
                                  ? "border-badge-danger"
                                  : "border-border-primary"
                              }`}
                              id="email"
                              {...register("Email")}
                            /> */}
                            <Input
                              type="email"
                              {...register("Email")}
                              className="w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary"
                              id="email"
                              placeholder="Enter email"
                              noWrapper={true}
                            />
                            {errors.Email && (
                              <Typography
                                variant="text2"
                                className="block w-full mt-1 text-badge-danger"
                              >
                                {errors.Email.message}
                              </Typography>
                            )}
                          </div>
                          <div className="mt-4">
                            <FormSubmitButton
                              loading={loading}
                              label="Send Reset Link"
                              loadingText="Sending..."
                              idleIconClass=""
                              className="w-full flex items-center justify-center gap-2"
                            />
                          </div>
                        </form>
                        <div className="mt-4 text-center">
                          <Link
                            to="/login"
                            className="text-text-secondary hover:text-theme-primary transition-colors text-sm"
                          >
                            Back to Login
                          </Link>
                        </div>
                      </div>
                    ) : (
                      <div className="p-2 text-center">
                        <Typography variant="text2" className="mb-6 text-gray-600">
                          If an account exists with this email address, you will receive password
                          reset instructions shortly.
                        </Typography>
                        <div className="mb-4">
                          <button
                            type="button"
                            onClick={handleResendEmail}
                            disabled={resendLoading || !submittedEmail}
                            className="text-theme-primary hover:underline text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {resendLoading ? "Sending..." : "Resend email"}
                          </button>
                        </div>
                        <div className="mt-4">
                          <Link to="/login">
                            <Button
                              type="button"
                              variant="primary"
                              className="w-full flex items-center justify-center gap-2"
                            >
                              <Typography variant="text2" component="span">
                                Back to Login
                              </Typography>
                            </Button>
                          </Link>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
